/* ==========================================================================
   Plant Store <> UnfoldCRO — Product page behaviour

   Two jobs:
     1. the gallery (arrows, thumbnails, and following the variant's image)
     2. revealing the sticky add-to-cart bar once the real button scrolls off

   Variant switching and add-to-cart are Dawn's (product-info.js /
   product-form.js) — this never touches them. Because product-info replaces
   the whole <product-info> element on every option change, everything here
   re-binds from a MutationObserver rather than binding once.
   ========================================================================== */
(function () {
  'use strict';

  if (window.ufcProductBound) return;
  window.ufcProductBound = true;

  var REDUCED = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // Centres the active thumbnail in its scrolling strip.
  function paintThumbs(thumbs, index) {
    Array.prototype.forEach.call(thumbs, function (thumb, i) {
      var on = i === index;
      thumb.classList.toggle('is-active', on);
      thumb.setAttribute('aria-current', on ? 'true' : 'false');
      if (!on) return;

      var strip = thumb.parentNode;
      if (!strip || strip.scrollWidth <= strip.clientWidth) return;
      var left = thumb.offsetLeft - (strip.clientWidth - thumb.offsetWidth) / 2;
      strip.scrollTo({ left: Math.max(0, left), behavior: REDUCED ? 'auto' : 'smooth' });
    });
  }

  // A video plays only while its own slide is the one on screen: the slide is
  // active, the gallery is actually in the viewport, and the visitor has not
  // asked for less motion. Everything else is paused -- otherwise the sound
  // carries on over the next photo, and a gallery scrolled past keeps pulling
  // video down a phone connection.
  function syncVideos(gallery) {
    var index = gallery.ufcActiveIndex || 0;
    var onScreen = gallery.ufcOnScreen !== false;

    Array.prototype.forEach.call(gallery.querySelectorAll('[data-ufc-slide]'), function (slide, i) {
      var video = slide.querySelector('video');
      if (!video) return;

      if (i === index && onScreen && !REDUCED) {
        // Autoplay is refused unless the video is muted; it is muted in the
        // markup, but a rejected promise here must not become an error.
        var started = video.play();
        if (started && started.catch) started.catch(function () {});
        return;
      }

      if (!video.paused) video.pause();
      // Back to the poster the next time it comes round, rather than the frame
      // it happened to stop on.
      if (i !== index) video.currentTime = 0;
    });
  }

  // Pauses everything while the gallery is off screen, and picks the active
  // slide back up when it returns.
  function watchGalleryVisibility(gallery) {
    if (!window.IntersectionObserver || gallery.ufcVisibilityBound) return;
    gallery.ufcVisibilityBound = true;

    new window.IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        gallery.ufcOnScreen = entry.isIntersecting;
        syncVideos(gallery);
      });
    }, { threshold: 0.25 }).observe(gallery);
  }

  // Used when Swiper is unavailable — the stage keeps working as a plain
  // one-slide-at-a-time gallery driven by .is-active.
  function manualGallery(gallery, slides, thumbs) {
    var index = parseInt(gallery.getAttribute('data-ufc-initial'), 10) || 0;

    function show(next) {
      index = (next + slides.length) % slides.length;
      Array.prototype.forEach.call(slides, function (slide, i) {
        slide.classList.toggle('is-active', i === index);
      });
      paintThumbs(thumbs, index);
      gallery.ufcActiveIndex = index;
      syncVideos(gallery);
    }

    gallery.ufcGoTo = show;
    watchGalleryVisibility(gallery);

    var prev = gallery.querySelector('[data-ufc-prev]');
    var next = gallery.querySelector('[data-ufc-next]');
    if (prev) prev.addEventListener('click', function () { show(index - 1); });
    if (next) next.addEventListener('click', function () { show(index + 1); });

    Array.prototype.forEach.call(thumbs, function (thumb) {
      thumb.addEventListener('click', function () {
        show(parseInt(thumb.dataset.ufcThumb, 10) || 0);
      });
    });

    var startX = null;
    var stage = gallery.querySelector('[data-ufc-stage]');
    if (!stage) return;

    stage.addEventListener('touchstart', function (event) {
      startX = event.changedTouches[0].clientX;
    }, { passive: true });

    stage.addEventListener('touchend', function (event) {
      if (startX === null) return;
      var delta = event.changedTouches[0].clientX - startX;
      if (Math.abs(delta) > 40) show(delta < 0 ? index + 1 : index - 1);
      startX = null;
    }, { passive: true });
  }

  // Moves whichever gallery is on the page to the media a variant carries.
  // Dawn dispatches a change on the form's hidden id input every time it
  // settles on a variant -- for the picker, the sticky bar, and a variant in
  // the URL alike -- so that one event covers every route in without reaching
  // into product-info's internals.
  function galleryMediaFor(gallery, variantId) {
    var data = gallery.querySelector('[data-ufc-variant-media]');
    if (!data || !variantId) return null;
    try {
      var map = JSON.parse(data.textContent);
      return map[String(variantId)] || null;
    } catch (e) {
      return null;
    }
  }

  function showVariantMedia(variantId) {
    var gallery = document.querySelector('[data-ufc-gallery]');
    if (!gallery || !gallery.ufcGoTo) return;

    var mediaId = galleryMediaFor(gallery, variantId);
    // A variant without an image of its own leaves the gallery where it is,
    // rather than jumping back to the first photo.
    if (!mediaId) return;

    var slides = gallery.querySelectorAll('[data-ufc-slide]');
    for (var i = 0; i < slides.length; i++) {
      if (String(slides[i].getAttribute('data-media-id')) === String(mediaId)) {
        gallery.ufcGoTo(i);
        return;
      }
    }
  }

  // Dawn replaces the contents of #price-<section id> for the new variant, but
  // the sticky bar carries its own copy of those figures. Mirrored from the
  // block Dawn updates rather than recalculated, so the money format stays the
  // shop's and there is one source of truth.
  function mirrorStickyPrice(root) {
    var source = root.querySelector('[id^="price-"]');
    var bar = root.querySelector('[data-ufc-sticky]');
    if (!source || !bar) return;

    var price = source.querySelector('.ufc-pdp__price');
    var compare = source.querySelector('.ufc-pdp__compare');

    Array.prototype.forEach.call(bar.querySelectorAll('.ufc-pdp__sticky-price'), function (el) {
      if (price) el.textContent = price.textContent.trim();
    });
    Array.prototype.forEach.call(bar.querySelectorAll('.ufc-pdp__sticky-compare'), function (el) {
      if (compare) {
        el.textContent = compare.textContent.trim();
        el.hidden = false;
      } else {
        // The new variant is not on offer; the struck-through figure has to go
        // with it rather than linger from the old one.
        el.hidden = true;
      }
    });
  }

  document.addEventListener('change', function (event) {
    var target = event.target;
    if (!target || target.name !== 'id' || !target.closest('form[action*="/cart/add"]')) return;
    showVariantMedia(target.value);

    // This fires from inside Dawn's update, before it has swapped the price in,
    // so the mirror waits for that synchronous work to finish.
    var root = target.closest('product-info') || document;
    setTimeout(function () { mirrorStickyPrice(root); }, 0);
  });

  /* --- The rating line's click ---------------------------------------------
     The buy box calls jdgm._openTabAndScrollToRevWidget() to jump to the
     reviews. Judge.me no longer ships that method -- window.jdgm is there, the
     function is not -- so the click threw and nothing moved. Rather than
     rewrite the call, the function it names is supplied when the app has not
     provided one; if a future version of the app brings its own back, that one
     is left alone. */
  (function () {
    window.jdgm = window.jdgm || {};
    if (typeof window.jdgm._openTabAndScrollToRevWidget === 'function') return;

    window.jdgm._openTabAndScrollToRevWidget = function () {
      var target = document.querySelector('.jdgm-rev-widg') ||
                   document.querySelector('#judgeme_product_reviews') ||
                   document.querySelector('.jdgm-widget');
      if (!target) return;

      // The name says "open tab" for a reason: if the reviews are ever put
      // inside an accordion, scrolling to a closed one would land on nothing.
      var holder = target.closest ? target.closest('details') : null;
      if (holder && !holder.open) holder.open = true;

      // Sticky headers cover the top of whatever you scroll to, so the target
      // is placed below them rather than under them.
      var header = document.querySelector('.ufc-header, .section-header, header');
      var offset = 16;
      if (header) {
        var hs = getComputedStyle(header);
        if (hs.position === 'fixed' || hs.position === 'sticky') {
          offset += header.getBoundingClientRect().height;
        }
      }

      function go(behavior) {
        var top = target.getBoundingClientRect().top + window.pageYOffset - offset;
        window.scrollTo({ top: Math.max(0, top), behavior: behavior });
      }

      go(REDUCED ? 'auto' : 'smooth');

      // Everything between here and the reviews is lazy: images and the app's
      // own carousel land after the scroll starts and push the target down the
      // page, so a single jump stops short of it. Corrected as it settles, and
      // dropped as soon as it holds still.
      var last = null;
      var tries = 0;
      var settle = setInterval(function () {
        var now = Math.round(target.getBoundingClientRect().top);
        if (last !== null && Math.abs(now - last) < 2) {
          clearInterval(settle);
          return;
        }
        last = now;
        if (++tries > 8) {
          clearInterval(settle);
          return;
        }
        go('auto');
      }, 250);
    };
  })();

  function initGallery(root) {
    var gallery = root.querySelector('[data-ufc-gallery]');
    if (!gallery) return;

    var stage = gallery.querySelector('[data-ufc-stage]');
    var slides = gallery.querySelectorAll('[data-ufc-slide]');
    var thumbs = gallery.querySelectorAll('[data-ufc-thumb]');
    if (!stage || slides.length < 2) return;

    // Swiper adds and removes nodes, and the MutationObserver below watches
    // the subtree — without this the observer would re-enter forever. Reading
    // the property off the element also means a freshly rendered stage (new
    // element, no .swiper) still initialises normally.
    if (stage.swiper) return;

    if (!window.Swiper) {
      if (gallery.dataset.ufcManual === '1') return;
      gallery.dataset.ufcManual = '1';
      manualGallery(gallery, slides, thumbs);
      return;
    }

    var prev = gallery.querySelector('[data-ufc-prev]');
    var next = gallery.querySelector('[data-ufc-next]');

    var options = {
      // Rendered active by the section, so Swiper has to start there too or it
      // would slide back to the first photo the moment it initialises.
      initialSlide: parseInt(gallery.getAttribute('data-ufc-initial'), 10) || 0,
      slidesPerView: 1,
      spaceBetween: 0,
      // Wraps last->first like the old arrows did, so neither arrow ever
      // becomes a dead end. rewind rather than loop, because loop remaps
      // indices and made thumbnail targeting unreliable.
      rewind: true,
      // The image sizes to its own shape, so the stage has to follow it.
      autoHeight: true,
      speed: REDUCED ? 0 : 380,
      grabCursor: true,
      watchSlidesProgress: true,
      // Our buttons already carry their own aria-labels; Swiper's a11y module
      // would overwrite them.
      a11y: { enabled: false },
      on: {
        init: function () {
          paintThumbs(thumbs, this.realIndex);
          gallery.ufcActiveIndex = this.realIndex;
          syncVideos(gallery);
        },
        slideChange: function () {
          paintThumbs(thumbs, this.realIndex);
          gallery.ufcActiveIndex = this.realIndex;
          syncVideos(gallery);
        }
      }
    };

    if (prev && next) options.navigation = { prevEl: prev, nextEl: next };

    watchGalleryVisibility(gallery);

    // Lay the slides out before Swiper measures them.
    stage.classList.add('is-swiper');

    var swiper = new window.Swiper(stage, options);
    gallery.ufcGoTo = function (i) { swiper.slideTo(i); };
    // The images are lazy and auto-height, so their intrinsic size can land
    // after init; recompute once they have.
    window.addEventListener('load', function () { swiper.update(); }, { once: true });

    Array.prototype.forEach.call(thumbs, function (thumb) {
      thumb.addEventListener('click', function () {
        swiper.slideTo(parseInt(thumb.dataset.ufcThumb, 10) || 0);
      });
    });
  }

  function initSticky(root) {
    var bar = root.querySelector('[data-ufc-sticky]');
    var buy = root.querySelector('[data-ufc-buy]');
    if (!bar || !buy || !('IntersectionObserver' in window)) return;
    if (bar.dataset.ufcBound === '1') return;
    bar.dataset.ufcBound = '1';

    var pastBuy = false;
    var atFooter = false;

    function paint() {
      bar.hidden = !pastBuy || atFooter;
    }

    new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          // Only once the real button has scrolled UP out of view — showing it
          // while the button is still below the fold would just duplicate it.
          pastBuy = !entry.isIntersecting && entry.boundingClientRect.top < 0;
          paint();
        });
      },
      { threshold: 0 }
    ).observe(buy);

    // The bar is fixed to the bottom of the viewport, so without this it sits
    // on top of the footer for the whole length of it. Once any of the footer
    // has scrolled into view the bar stands down and the footer is readable.
    var footer = document.querySelector('.ufc-footer, .shopify-section-group-footer-group');

    // Deliberately not IntersectionObserver here. Intersection is a window: it
    // opens when the footer arrives and closes again once the footer has passed
    // out of the top of the screen, which brought the bar back over anything
    // sitting below the footer -- an app block, a script's injected markup, or
    // just an overscroll bounce on a phone. What is wanted is a threshold, not
    // a window: from the moment the footer's top edge reaches the bottom of the
    // screen it has "appeared", and it stays appeared however much further the
    // page is scrolled, because the top only goes more negative.
    var ticking = false;

    function readFooter() {
      ticking = false;
      var vh = window.innerHeight || document.documentElement.clientHeight;
      var next = false;

      if (footer) next = footer.getBoundingClientRect().top <= vh;

      // Belt and braces for the end of the page. On a phone the viewport height
      // moves as the browser chrome hides, and an overscroll bounce can carry
      // the page past its own end -- so the last screenful counts as the footer
      // whatever the measurements say, and this still holds on a page that has
      // no footer element to watch at all.
      var doc = Math.max(
        document.body.scrollHeight,
        document.documentElement.scrollHeight,
        document.documentElement.offsetHeight
      );
      if (window.pageYOffset + vh >= doc - 2) next = true;

      if (next === atFooter) return;
      atFooter = next;
      paint();
    }

    function onScroll() {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(readFooter);
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    // The footer can start on screen on a short page, and its height settles as
    // images and app blocks load, so take a reading now and after load too.
    readFooter();
    window.addEventListener('load', readFooter);
  }

  /* Sticky bar: the variant pill, its chooser, and the quantity stepper.

     Nothing here owns state. Choosing a variant clicks the matching control in
     Dawn's own picker, so product-info re-renders and the whole page follows;
     the stepper writes to the real quantity input in the buy box, which is
     what the form posts. That keeps one source of truth in both cases. */
  function initStickyBar(root) {
    var bar = root.querySelector('[data-ufc-sticky]');
    if (!bar || bar.dataset.ufcStickyBound === '1') return;
    bar.dataset.ufcStickyBound = '1';

    var pill = bar.querySelector('[data-ufc-sticky-pill]');
    var menu = bar.querySelector('[data-ufc-sticky-menu]');

    if (pill && menu) {
      var close = function () {
        menu.hidden = true;
        pill.setAttribute('aria-expanded', 'false');
      };

      pill.addEventListener('click', function (event) {
        event.stopPropagation();
        var open = menu.hidden;
        menu.hidden = !open;
        pill.setAttribute('aria-expanded', open ? 'true' : 'false');
      });

      document.addEventListener('click', function (event) {
        if (!menu.hidden && !bar.contains(event.target)) close();
      });

      document.addEventListener('keydown', function (event) {
        if (event.key === 'Escape') close();
      });

      Array.prototype.forEach.call(
        bar.querySelectorAll('[data-ufc-sticky-variant]'),
        function (option) {
          option.addEventListener('click', function () {
            close();
            selectVariant(root, option.getAttribute('data-ufc-sticky-variant'));
          });
        }
      );
    }

    var input = root.querySelector('.quantity__input');
    var value = bar.querySelector('[data-ufc-sticky-qty-value]');
    var down = bar.querySelector('[data-ufc-sticky-qty-down]');
    var up = bar.querySelector('[data-ufc-sticky-qty-up]');

    if (input && value) {
      var sync = function () {
        value.textContent = input.value || '1';
      };

      var nudge = function (delta) {
        var min = parseInt(input.min, 10) || 1;
        var step = parseInt(input.step, 10) || 1;
        var next = (parseInt(input.value, 10) || min) + delta * step;
        if (next < min) next = min;
        if (input.max && next > parseInt(input.max, 10)) next = parseInt(input.max, 10);
        input.value = next;
        // Dawn listens for change to revalidate against inventory rules.
        input.dispatchEvent(new Event('change', { bubbles: true }));
        sync();
      };

      if (down) down.addEventListener('click', function () { nudge(-1); });
      if (up) up.addEventListener('click', function () { nudge(1); });
      input.addEventListener('change', sync);
      sync();
    }
  }

  /* Hands the choice to Dawn's variant picker. Pills are radios, dropdowns are
     a <select>; either way we set it and fire change, and product-info does the
     rest. */
  function selectVariant(root, variantId) {
    if (!variantId) return;

    var picker = root.querySelector('variant-selects');
    if (!picker) return;

    // Match on the variant's own option values, from the JSON the section
    // emits alongside the bar.
    var data = document.querySelector('[data-ufc-variants]');
    var list = null;
    if (data) {
      try {
        list = JSON.parse(data.textContent);
      } catch (e) {
        list = null;
      }
    }

    var wanted = null;
    if (list) {
      for (var i = 0; i < list.length; i++) {
        if (String(list[i].id) === String(variantId)) {
          wanted = list[i].options;
          break;
        }
      }
    }

    if (!wanted) {
      // Nothing to match against — fall back to a plain navigation, which is
      // always correct even if it costs a page load.
      var url = root.getAttribute('data-product-url') || window.location.pathname;
      window.location.href = url + '?variant=' + variantId;
      return;
    }

    wanted.forEach(function (optionValue) {
      var inputs = picker.querySelectorAll('input[type="radio"], option');
      Array.prototype.forEach.call(inputs, function (el) {
        if (el.value !== optionValue) return;
        if (el.tagName === 'OPTION') {
          var select = el.closest('select');
          if (select && select.value !== optionValue) {
            select.value = optionValue;
            select.dispatchEvent(new Event('change', { bubbles: true }));
          }
        } else if (!el.checked) {
          el.checked = true;
          el.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });
    });
  }

  function initPin(root) {
    var wrap = root.querySelector('[data-ufc-pin]');
    if (!wrap) return;
    if (wrap.dataset.ufcBound === '1') return;
    wrap.dataset.ufcBound = '1';

    var input = wrap.querySelector('[data-ufc-pin-input]');
    var button = wrap.querySelector('[data-ufc-pin-check]');
    var message = root.querySelector('[data-ufc-pin-msg]');
    if (!input || !button || !message) return;

    function check() {
      var value = input.value.replace(/\D/g, '').slice(0, 6);
      input.value = value;

      var valid = /^[1-9][0-9]{5}$/.test(value);
      message.hidden = false;
      message.classList.toggle('is-error', !valid);
      message.textContent = valid
        ? String(message.dataset.ok).split('[pin]').join(value)
        : message.dataset.bad;
    }

    button.addEventListener('click', check);
    input.addEventListener('keydown', function (event) {
      if (event.key === 'Enter') {
        event.preventDefault();
        check();
      }
    });
  }

  function initGift(root) {
    var toggle = root.querySelector('[data-ufc-gift-toggle]');
    var panel = root.querySelector('[data-ufc-gift-panel]');
    if (!toggle || !panel) return;
    if (toggle.dataset.ufcBound === '1') return;
    toggle.dataset.ufcBound = '1';

    var field = panel.querySelector('[data-ufc-gift-input]');
    var counter = panel.querySelector('[data-ufc-gift-count]');

    toggle.addEventListener('change', function () {
      panel.hidden = !toggle.checked;
      // Keeping it disabled while closed stops an empty gift-note property
      // being attached to the order.
      if (field) {
        field.disabled = !toggle.checked;
        if (toggle.checked) field.focus();
      }
    });

    if (field && counter) {
      field.addEventListener('input', function () {
        counter.textContent = 255 - field.value.length;
      });
    }
  }

  /* The variant pills are one nowrap scrolling line (see ufc-product.css), so
     long option values run off the edge instead of wrapping. Touch scrolls it
     natively; this adds the same drag on a mouse.

     Deliberately no setPointerCapture. Capturing retargets the pointer stream
     to the track, so the browser computes the click target as the track rather
     than the <label> under the cursor — the label's default action never runs
     and the variant cannot be selected at all. Plain listeners on the track
     keep the label as the click target. */
  function initPillDrag(root) {
    var tracks = root.querySelectorAll('.ufc-pdp__variants .product-form__input--pill');

    Array.prototype.forEach.call(tracks, function (track) {
      if (track.dataset.ufcDrag === '1') return;
      track.dataset.ufcDrag = '1';

      var down = false;
      var dragging = false;
      var startX = 0;
      var startLeft = 0;
      var moved = 0;

      function stop() {
        down = false;
        dragging = false;
        moved = 0;
        track.classList.remove('is-dragging');
      }

      track.addEventListener('pointerdown', function (event) {
        // Touch and pen already scroll natively — hijacking them would fight
        // the momentum scrolling.
        if (event.pointerType !== 'mouse' || event.button !== 0) return;
        if (track.scrollWidth <= track.clientWidth) return;

        down = true;
        dragging = false;
        moved = 0;
        startX = event.clientX;
        startLeft = track.scrollLeft;
      });

      track.addEventListener('pointermove', function (event) {
        if (!down) return;

        var delta = event.clientX - startX;
        moved = Math.abs(delta);

        // A few pixels of slop, so a click with a shaky hand still selects.
        if (!dragging && moved > 4) {
          dragging = true;
          track.classList.add('is-dragging');
        }
        if (!dragging) return;

        event.preventDefault();
        track.scrollLeft = startLeft - delta;
      });

      track.addEventListener('pointerup', function () {
        down = false;
        dragging = false;
        track.classList.remove('is-dragging');
        // `moved` is left alone here — the click handler below runs after
        // pointerup and needs it to decide whether this was a drag.
      });

      track.addEventListener('pointerleave', stop);
      track.addEventListener('pointercancel', stop);

      // A drag that ends over a label would otherwise select that variant.
      track.addEventListener(
        'click',
        function (event) {
          if (moved > 4) {
            event.preventDefault();
            event.stopPropagation();
          }
          moved = 0;
        },
        true
      );
    });
  }

  /* Gardening tip guide. The dialog is a native <dialog>, so showModal() handles
     the backdrop, the focus trap and the Escape key — this only wires the open
     and close buttons, the click-outside, and the download.

     Download goes through fetch + blob because the `download` attribute is
     ignored cross-origin, and the images are served from the Shopify CDN. That
     CDN answers with Access-Control-Allow-Origin: *, so the fetch is allowed;
     if it ever fails the file simply opens in a new tab instead, which is what
     an anchor would have done anyway. */
  function initGuide() {
    var dialog = document.querySelector('[data-ufc-guide]');
    if (!dialog || dialog.dataset.ufcBound === '1') return;
    dialog.dataset.ufcBound = '1';

    var openers = document.querySelectorAll('[data-ufc-guide-open]');
    var closer = dialog.querySelector('[data-ufc-guide-close]');
    var download = dialog.querySelector('[data-ufc-guide-download]');
    var panel = dialog.querySelector('.ufc-guide__panel');

    function open() {
      if (typeof dialog.showModal === 'function') dialog.showModal();
      else dialog.setAttribute('open', '');
      // <dialog> does not stop the page behind it scrolling.
      document.body.style.overflow = 'hidden';
    }

    function close() {
      if (typeof dialog.close === 'function') dialog.close();
      else dialog.removeAttribute('open');
      document.body.style.overflow = '';
    }

    Array.prototype.forEach.call(openers, function (btn) {
      btn.addEventListener('click', open);
    });
    if (closer) closer.addEventListener('click', close);
    dialog.addEventListener('close', function () {
      document.body.style.overflow = '';
    });

    // Clicking the backdrop closes it. The backdrop is the dialog itself, so a
    // click landing outside the panel is the test.
    dialog.addEventListener('click', function (event) {
      if (panel && !panel.contains(event.target)) close();
    });

    if (!download) return;

    download.addEventListener('click', function () {
      var items = dialog.querySelectorAll('[data-ufc-guide-src]');
      if (!items.length) return;

      download.setAttribute('aria-busy', 'true');

      var jobs = Array.prototype.map.call(items, function (el, i) {
        var url = el.getAttribute('data-ufc-guide-src');
        // handleize leaves symbols like ® in place, which makes for an odd
        // filename, so the base is reduced to plain ascii words here.
        var base = (el.getAttribute('data-ufc-guide-name') || 'gardening-tips')
          .replace(/[^a-z0-9]+/gi, '-')
          .replace(/^-+|-+$/g, '')
          .toLowerCase() || 'gardening-tips';
        var name = items.length > 1 ? base + '-' + (i + 1) : base;

        return fetch(url)
          .then(function (response) {
            if (!response.ok) throw new Error(String(response.status));
            return response.blob();
          })
          .then(function (blob) {
            var href = URL.createObjectURL(blob);
            var a = document.createElement('a');
            var ext = (blob.type.split('/')[1] || 'jpg').replace('jpeg', 'jpg');
            a.href = href;
            a.download = name + '.' + ext;
            document.body.appendChild(a);
            a.click();
            a.remove();
            // Revoked on a timeout, not immediately: Safari has not always
            // finished reading the blob by the time click() returns.
            window.setTimeout(function () { URL.revokeObjectURL(href); }, 4000);
          })
          .catch(function () {
            window.open(url, '_blank', 'noopener');
          });
      });

      Promise.all(jobs).then(function () {
        download.removeAttribute('aria-busy');
      });
    });
  }

  function init() {
    var root = document.querySelector('product-info.ufc-pdp');
    if (!root) return;
    initGallery(root);
    initSticky(root);
    initStickyBar(root);
    initPin(root);
    initGift(root);
    initPillDrag(root);
    initGuide();
  }

  function watch() {
    var host = document.querySelector('product-info.ufc-pdp');
    if (!host || !host.parentNode || !window.MutationObserver) return;

    // product-info swaps itself out on every variant change, so re-bind
    // against whatever element is in the DOM now.
    new MutationObserver(function () {
      init();
    }).observe(host.parentNode, { childList: true, subtree: true });
  }

  function boot() {
    init();
    watch();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
